/**
 * A range as weights over the 1,326 two-card combinations.
 *
 * The trainer's ranges live over the 169 strategically distinct hands, which is right
 * for preflop: before a board exists, AhKh and AsKs are the same hand. After a flop
 * they are not, and the difference is not cosmetic. A bot holding two hearts on a
 * two-heart board must know its opponent is LESS likely to hold the flush draw,
 * because the bot is holding two of the cards that make it. Card removal is most of
 * what separates a bot that reads a board from one that recites a chart.
 *
 * So: 1,326 slots, indexed by an ordered pair of card ids. Dense, because a dense
 * Float32Array of 1,326 is smaller than a Map of the few hundred that survive a
 * filter, and it never allocates during a decision.
 */
import { HANDS, combosOf, rankOf, suitOf } from "../pipeline/solve/evaluator";
import type { Card } from "./types";

export const COMBO_COUNT = 1326;

/** combo id for two distinct cards, order-independent. */
export function comboId(a: Card, b: Card): number {
  const lo = a < b ? a : b;
  const hi = a < b ? b : a;
  /**
   * Triangular index: slots for (0,1)…(0,51) then (1,2)…, unique and gapless.
   * The offset for `lo` is the count of pairs before it, sum(51-k) for k<lo, which is
   * lo*(103-lo)/2. Getting that constant wrong by two collides (0,51) with (1,2) and
   * every combo above them — silently, since both are legal ids.
   */
  return (lo * (103 - lo)) / 2 + (hi - lo - 1);
}

const CARDS_OF: [Card, Card][] = (() => {
  const out: [Card, Card][] = new Array(COMBO_COUNT);
  for (let a = 0; a < 52; a++) {
    for (let b = a + 1; b < 52; b++) out[comboId(a, b)] = [a, b];
  }
  return out;
})();

/** The two cards of a combo id. Shared, frozen arrays — never mutate the result. */
export function comboCards(id: number): [Card, Card] { return CARDS_OF[id]!; }

/** 169-hand code for a combo, e.g. "AKs". */
export function comboCode(id: number): string {
  const [a, b] = CARDS_OF[id]!;
  const ra = rankOf(a), rb = rankOf(b);
  const hi = Math.max(ra, rb), lo = Math.min(ra, rb);
  const R = "23456789TJQKA";
  if (ra === rb) return `${R[hi]}${R[lo]}`;
  return `${R[hi]}${R[lo]}${suitOf(a) === suitOf(b) ? "s" : "o"}`;
}

/** combo ids belonging to each 169-hand code, precomputed once. */
export const COMBOS_OF_CODE: Record<string, number[]> = (() => {
  const out: Record<string, number[]> = {};
  for (const code of HANDS) out[code] = combosOf(code).map(([a, b]) => comboId(a, b));
  return out;
})();

export class Range {
  readonly w: Float32Array;

  private constructor(w: Float32Array) { this.w = w; }

  static empty(): Range { return new Range(new Float32Array(COMBO_COUNT)); }

  static full(weight = 1): Range {
    const w = new Float32Array(COMBO_COUNT);
    w.fill(weight);
    return new Range(w);
  }

  /** From 169-hand weights, spread evenly across each hand's combos. */
  static fromCodes(weights: Record<string, number>): Range {
    const w = new Float32Array(COMBO_COUNT);
    for (const [code, weight] of Object.entries(weights)) {
      if (!(weight > 0)) continue;
      const ids = COMBOS_OF_CODE[code];
      if (!ids) throw new Error(`unknown hand code: ${code}`);
      for (const id of ids) w[id] = weight;
    }
    return new Range(w);
  }

  clone(): Range { return new Range(this.w.slice()); }

  weight(id: number): number { return this.w[id]!; }

  /** Total combos by weight — the range's "width" in combos, not in percent. */
  mass(): number {
    let t = 0;
    for (let i = 0; i < COMBO_COUNT; i++) t += this.w[i]!;
    return t;
  }

  /** Share of all 1,326 combos, which is what people mean by "he opens 22%". */
  percent(): number { return (this.mass() / COMBO_COUNT) * 100; }

  /**
   * Zero every combo that uses one of these cards.
   *
   * Called with the board and with the holder's own cards. Skipping it is the classic
   * bot bug: it lets an opponent "hold" a card that is face up on the table, which
   * inflates every flush and straight the bot thinks it might be facing.
   */
  removeCards(cards: readonly Card[]): this {
    for (const c of cards) {
      for (let other = 0; other < 52; other++) {
        if (other === c) continue;
        this.w[comboId(c, other)] = 0;
      }
    }
    return this;
  }

  /** Multiply weights in place by a per-combo factor. The workhorse of range narrowing. */
  reweight(f: (id: number) => number): this {
    for (let i = 0; i < COMBO_COUNT; i++) {
      const cur = this.w[i]!;
      if (cur > 0) this.w[i] = cur * f(i);
    }
    return this;
  }

  /** Scale so the widest combo sits at 1. Keeps relative shape, restores comparability. */
  normalise(): this {
    let max = 0;
    for (let i = 0; i < COMBO_COUNT; i++) if (this.w[i]! > max) max = this.w[i]!;
    if (max > 0 && max !== 1) {
      for (let i = 0; i < COMBO_COUNT; i++) if (this.w[i]! > 0) this.w[i] = this.w[i]! / max;
    }
    return this;
  }

  /** Combos with any weight, as ids. Allocates — call it outside hot loops. */
  ids(): number[] {
    const out: number[] = [];
    for (let i = 0; i < COMBO_COUNT; i++) if (this.w[i]! > 0) out.push(i);
    return out;
  }

  /**
   * A cumulative-weight table for sampling. Built once per decision and reused across
   * every Monte Carlo trial, because rebuilding it per trial was most of the cost.
   */
  sampler(): { ids: Int32Array; cum: Float64Array; total: number } {
    const ids: number[] = [];
    const cum: number[] = [];
    let total = 0;
    for (let i = 0; i < COMBO_COUNT; i++) {
      const x = this.w[i]!;
      if (x <= 0) continue;
      total += x;
      ids.push(i);
      cum.push(total);
    }
    return { ids: Int32Array.from(ids), cum: Float64Array.from(cum), total };
  }
}

/** Draw a combo id from a prepared sampler. Binary search over the cumulative weights. */
export function sampleCombo(
  s: { ids: Int32Array; cum: Float64Array; total: number }, u: number
): number {
  if (s.total <= 0) return -1;
  const target = u * s.total;
  let lo = 0, hi = s.cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (s.cum[mid]! < target) lo = mid + 1; else hi = mid;
  }
  return s.ids[lo]!;
}
